import { useMainStore } from '@/store'
import { addTrailingSlash } from '@/utils/url-action'
import { useNuxtApp } from 'nuxt/app'

const useInternalFetch = async function () {
  let url, method, params, options
  if (arguments.length === 4) {
    url = arguments[0]
    method = arguments[1]
    params = arguments[2]
    options = arguments[3]
  } else if (arguments.length === 3) {
    url = arguments[0]
    method = arguments[1]
    options = arguments[2]
  } else {
    throw new Error('Invalid arguments')
  }
  const app = useNuxtApp()
  const store = useMainStore()
  const baseURL = import.meta.client ? import.meta.env.BASE_API_URL : import.meta.env.BASE_SSR_API_URL

  const defaults =
    method === 'get'
      ? {
          baseURL,
          method,
          credentials: 'include',
          onRequest({ request, options }) {
            // FIXME: useFetchのバグかも知れないので、修正されたらこの処理も削除する
            // Nuxtへのリクエストヘッダのcontent-lengthがバイパスされる。
            // GETメソッドの場合、bodyはないのでcontent-lengthヘッダを削除する
            if (options.headers && options.headers['content-length']) delete options.headers['content-length']
          }
        }
      : {
          baseURL,
          method,
          credentials: 'include'
        }

  if (params) defaults.body = params
  defaults.headers = setupDefaultHeaders(options.headers, store)

  const { data: res, error, refresh } = await useFetch(addTrailingSlash(url), defaults)
  if (error.value) {
    if (error.value.statusCode === 401) store.SET_STATE({ key: 'auth_info', value: null })
    // Nuxt 4: FetchError#status is a read-only getter that already returns statusCode;
    // assigning to it throws ("Cannot set property status ... which has only a getter").
    throw { response: error.value }
  }
  if (res.value == null) await refresh()
  const resFetch = { data: res.value }
  return resFetch
}

const useAsyncFetch = async function () {
  let url, method, params, options
  if (arguments.length === 4) {
    url = arguments[0]
    method = arguments[1]
    params = arguments[2]
    options = arguments[3]
  } else if (arguments.length === 3) {
    url = arguments[0]
    method = arguments[1]
    options = arguments[2]
  } else {
    throw new Error('Invalid arguments')
  }
  const app = useNuxtApp()
  const store = useMainStore()
  const baseURL = import.meta.client ? import.meta.env.BASE_API_URL : import.meta.env.BASE_SSR_API_URL

  const defaults = {
    baseURL,
    method,
    credentials: 'include'
  }

  if (params) defaults.body = params
  defaults.headers = setupDefaultHeaders(options.headers, store)

  const { data: res, error, refresh } = await useAsyncData(() => $fetch(addTrailingSlash(url), defaults))
  if (error.value) {
    if (error.value.statusCode === 401) store.SET_STATE({ key: 'auth_info', value: null })
    // Nuxt 4: FetchError#status is a read-only getter that already returns statusCode;
    // assigning to it throws ("Cannot set property status ... which has only a getter").
    throw { response: error.value }
  }
  if (res.value == null) await refresh()
  const resFetch = { data: res.value }
  return resFetch
}

const useAsyncFetchV2 = async function () {
  let url, method, params, options
  if (arguments.length === 4) {
    url = arguments[0]
    method = arguments[1]
    params = arguments[2]
    options = arguments[3]
  } else if (arguments.length === 3) {
    url = arguments[0]
    method = arguments[1]
    options = arguments[2]
  } else {
    throw new Error('Invalid arguments')
  }
  const app = useNuxtApp()
  const store = useMainStore()
  const baseURL = import.meta.client ? import.meta.env.BASE_API_URL : import.meta.env.BASE_SSR_API_URL

  const defaults = {
    baseURL,
    method,
    credentials: 'include'
  }

  if (params) defaults.body = params
  defaults.headers = setupDefaultHeaders(options.headers, store)

  const addedTrailingSlashUrl = addTrailingSlash(url)
  const key = addedTrailingSlashUrl.replace(baseURL, '')
  const { data: res, error, refresh } = await useAsyncData(key, () => $fetch(addedTrailingSlashUrl, defaults))
  if (error.value) {
    if (error.value.statusCode === 401) store.SET_STATE({ key: 'auth_info', value: null })
    // Nuxt 4: FetchError#status is a read-only getter that already returns statusCode;
    // assigning to it throws ("Cannot set property status ... which has only a getter").
    throw { response: error.value }
  }
  if (res.value == null) await refresh()
  const resFetch = { data: res.value }
  return resFetch
}

const setupDefaultHeaders = (optionHeaders, store) => {
  const headers = optionHeaders && Object.keys(optionHeaders).length > 0 ? optionHeaders : {}

  // Without this process, when SSR mode, Nuxt server can't pass the cookie to the API server.
  if (import.meta.server) {
    let cookieString = ''
    const cookieObject = store.ssrCookie
    const cookieStringArr = []
    for (const [key, value] of Object.entries(cookieObject)) {
      cookieStringArr.push(`${key}=${encodeURIComponent(value)}`)
    }
    cookieString = cookieStringArr.join('; ')
    headers.cookie = cookieString
  }

  return headers
}

/**
 * useAsyncDataを使わない、どこからでも呼び出せるfetchメソッド
 * Promise.all後や.then()チェーン内でも使用可能
 */
const usePlainFetch = async function () {
  let url, method, params, options
  if (arguments.length === 4) {
    url = arguments[0]
    method = arguments[1]
    params = arguments[2]
    options = arguments[3]
  } else if (arguments.length === 3) {
    url = arguments[0]
    method = arguments[1]
    options = arguments[2]
  } else {
    throw new Error('Invalid arguments')
  }
  const store = useMainStore()
  const baseURL = import.meta.client ? import.meta.env.BASE_API_URL : import.meta.env.BASE_SSR_API_URL

  const defaults = {
    baseURL,
    method,
    credentials: 'include'
  }

  if (params) defaults.body = params
  defaults.headers = setupDefaultHeaders(options.headers, store)

  try {
    const data = await $fetch(addTrailingSlash(url), defaults)
    return { data }
  } catch (error) {
    if (error.statusCode === 401) store.SET_STATE({ key: 'auth_info', value: null })
    // Nuxt 4: FetchError#status is a read-only getter (already returns statusCode); do not assign.
    throw { response: error }
  }
}

export { useInternalFetch, useAsyncFetch, useAsyncFetchV2, usePlainFetch }
